import React from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, Fuel, Gauge, MapPin } from "lucide-react";
import { formatMiles, formatUSD, titleCase } from "@/lib/format";

const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1494976388531-d1058494cdd8?auto=format&fit=crop&w=1200&q=80";

export const VehicleCard = ({ vehicle, dealerName, isComingSoon = false }) => {
  const img = vehicle.images?.[0] || FALLBACK_IMG;
  return (
    <Link
      to={`/vehicle/${vehicle.id}`}
      className={`group bg-white rounded-2xl border border-slate-200 overflow-hidden transition-all hover:-translate-y-1 hover:shadow-xl hover:border-slate-300 flex flex-col h-full ${
        isComingSoon ? "opacity-95" : ""
      }`}
      data-testid={`vehicle-card-${vehicle.id}`}
      data-coming-soon={isComingSoon ? "true" : undefined}
    >
      <div className="relative aspect-[4/3] bg-slate-100 overflow-hidden">
        <img
          src={img}
          alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />

        {/* Coming Soon overlay — diagonal black bar with white text across
            the vehicle image. Purely CSS via rotate + translate, no image
            assets, no JS runtime cost. Sits above photo/badges (z-30). */}
        {isComingSoon && (
          <div
            className="absolute inset-0 pointer-events-none flex items-center justify-center overflow-hidden"
            data-testid="vehicle-card-coming-soon-overlay"
          >
            <div className="w-[160%] bg-slate-900/85 text-white text-center py-2 shadow-2xl -rotate-[18deg] backdrop-blur-[1px]">
              <span className="font-display text-lg font-bold tracking-[0.4em] uppercase text-white">
                Coming Soon
              </span>
            </div>
          </div>
        )}

        {vehicle.condition === "certified" && !isComingSoon && (
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-white/90 backdrop-blur text-brand text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border border-slate-200">
            <BadgeCheck size={12} /> Certified
          </span>
        )}
        {vehicle.msrp && vehicle.msrp > vehicle.price && !isComingSoon && (
          <span className="absolute top-3 right-3 bg-gold text-white text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
            Save {formatUSD(vehicle.msrp - vehicle.price)}
          </span>
        )}
        {/* Phase 10 — stock number, always visible bottom-left for ops staff.
            Sticky-colored to survive the coming-soon overlay backdrop. */}
        {vehicle.stock_number && (
          <span
            className="absolute bottom-3 left-3 bg-slate-950/85 text-white text-[10px] font-mono font-bold px-2 py-1 rounded-full"
            data-testid={`vehicle-card-stock-${vehicle.id}`}
            title={`Stock #${vehicle.stock_number}`}
          >
            #{vehicle.stock_number}
          </span>
        )}
      </div>

      <div className="p-5 flex flex-col flex-grow">
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="font-display text-lg font-semibold text-slate-900 tracking-tight truncate">
            {vehicle.year} {vehicle.make} {vehicle.model}
          </h3>
        </div>
        <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">
          {vehicle.trim || titleCase(vehicle.body_style)}
        </p>

        <div className="mt-4 flex items-center gap-4 text-xs text-slate-600">
          <span className="inline-flex items-center gap-1">
            <Gauge size={13} className="text-slate-400" /> {formatMiles(vehicle.mileage)}
          </span>
          <span className="inline-flex items-center gap-1">
            <Fuel size={13} className="text-slate-400" /> {titleCase(vehicle.fuel_type)}
          </span>
        </div>

        <div className="mt-4 pt-4 border-t border-slate-100 flex items-end justify-between">
          <div>
            {vehicle.msrp && vehicle.msrp > vehicle.price && (
              <span className="block text-xs text-slate-400 line-through">
                {formatUSD(vehicle.msrp)}
              </span>
            )}
            <span className="font-display text-2xl font-bold text-slate-900">
              {formatUSD(vehicle.price)}
            </span>
          </div>
          {dealerName && (
            <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 max-w-[45%] truncate">
              <MapPin size={11} /> {dealerName}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};
